import {
  ArrowRight,
  ArrowRightLeft,
  Ban,
  Bell,
  CheckCircle2,
  ChefHat,
  CircleDot,
  Clock,
  Combine,
  DollarSign,
  Eye,
  Link,
  Plus,
  Split,
  Unlock,
  Users,
} from 'lucide-react';
import type { MesaVista, PedidoVista } from '@shared/types';
import { formatPrecio } from '@renderer/utils/formatters';
import { ReactivarMesaButton } from '@renderer/components/mesas/MesaActionsMenu';
import { MINUTOS_AVISO_CUENTA, minutosConCuenta } from '@shared/cuentaMesa';

/**
 * Sprint 9.0 — Componente único para visualizar y operar mesas.
 * Sprint 9.2 — Soporta estado `fuera_de_servicio` + menú kebab con acciones
 * contextuales (mover, juntar, dividir, liberar, fuera/reactivar).
 *
 * Tres modos:
 *   - `select`     → wizard de pedido. Solo se puede tocar la mesa libre.
 *                    Mesas fuera de servicio se OCULTAN (no son elegibles).
 *   - `monitor`    → página /mesas. Card completa con acciones y kebab.
 *   - `monitor` + `compact` → Dashboard. Card mínima, click navega.
 */

export interface MesasGridProps {
  mesas: MesaVista[];
  /** Requerido en modo `monitor` para mostrar badges y total. Indexado por mesaId. */
  pedidoPorMesa?: Map<number, PedidoVista>;
  mode: 'select' | 'monitor';
  /** Modo monitor compacto (Dashboard). Oculta acciones secundarias. */
  compact?: boolean;
  monedaSimbolo?: string;
  locale?: string;

  // Callbacks operativos (modo monitor)
  onMesaLibre?: (mesaId: number) => void;
  onMesaOcupada?: (mesaId: number, pedidoId: number | null) => void;
  /**
   * Sprint 111 — Mover el flujo de cocina sin salir de Mesas.
   *
   * Quien atiende sola no puede irse a la pantalla de Cocina para dar un paso y volver: cada
   * cambio de pantalla es perder de vista el salón.
   */
  onMarcarListo?: (pedidoId: number) => void;
  onEntregarEnMesa?: (pedidoId: number) => void;
  onCobrar?: (pedidoId: number) => void;
  onVerTicket?: (pedidoId: number) => void;
  onMarcarCuenta?: (pedidoId: number, solicitada: boolean) => void;
  /** Sprint 28 — Anexar productos a un pedido de mesa (comer_aqui) en curso. */
  onAgregarProductos?: (pedidoId: number, mesaId: number) => void;
  /** Sprint 29 — Volver a juntar una cuenta dividida (cuando la mesa muestra un sub-pedido). */
  onReunificarCuenta?: (pedidoId: number) => void;

  // Sprint 9.2 — Acciones del kebab (sólo modo monitor no compact)
  onMoverPedido?: (pedidoId: number, mesaActualId: number) => void;
  onJuntarMesa?: (pedidoId: number, mesaPrincipalId: number) => void;
  onDividirCuenta?: (pedidoId: number) => void;
  onLiberarMesa?: (pedidoId: number, mesaNumero: number) => void;
  onFueraServicio?: (mesaId: number, mesaNumero: number) => void;
  onReactivar?: (mesaId: number) => void;
}

export function MesasGrid(props: MesasGridProps) {
  const { mesas, mode } = props;

  if (mesas.length === 0) {
    return (
      <div className="card text-center text-slate-400 py-12">
        <Users className="mx-auto mb-3 h-12 w-12 opacity-30" />
        <p>No hay mesas registradas.</p>
      </div>
    );
  }

  if (mode === 'select') {
    // Sprint 9.2: mesas fuera de servicio no son elegibles → ocultas.
    const elegibles = mesas.filter((m) => m.estado !== 'fuera_de_servicio');
    const disponibles = elegibles.filter((m) => m.estado === 'disponible');
    const ocupadas = elegibles.filter((m) => m.estado === 'ocupada');
    return (
      <div className="space-y-6">
        {disponibles.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-green-700">
              Disponibles ({disponibles.length})
            </h3>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {disponibles.map((m) => (
                <button
                  key={m.id}
                  onClick={() => props.onMesaLibre?.(m.id)}
                  className="card flex flex-col items-center gap-1 hover:border-primary hover:shadow-md transition cursor-pointer p-4"
                >
                  <div className="text-2xl">✅</div>
                  <div className="text-xl font-bold">Mesa #{m.numero}</div>
                  <div className="text-xs text-slate-500">{m.capacidad} pers.</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {ocupadas.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-orange-700">
              Ocupadas — solo informativo ({ocupadas.length})
            </h3>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {ocupadas.map((m) => (
                <div
                  key={m.id}
                  className="card flex flex-col items-center gap-1 opacity-60 cursor-not-allowed p-4 bg-orange-50"
                  title={
                    m.pedidoActivoNumero
                      ? `Pedido #${String(m.pedidoActivoNumero).padStart(5, '0')} · ${tiempoTranscurrido(m.pedidoActivoFechaCreacion)}`
                      : undefined
                  }
                >
                  <div className="text-2xl">🍽️</div>
                  <div className="text-xl font-bold">Mesa #{m.numero}</div>
                  <div className="text-xs text-orange-700 uppercase">ocupada</div>
                  {m.pedidoActivoFechaCreacion && (
                    <div className="text-[10px] text-orange-600">
                      {tiempoTranscurrido(m.pedidoActivoFechaCreacion)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  // mode === 'monitor'
  return (
    <div
      className={`grid gap-3 ${
        props.compact
          ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
          : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
      }`}
    >
      {mesas.map((m) => {
        const pedido = props.pedidoPorMesa?.get(m.id) ?? null;
        return (
          <MesaCard
            key={m.id}
            mesa={m}
            pedido={pedido}
            compact={props.compact ?? false}
            monedaSimbolo={props.monedaSimbolo ?? '$'}
            locale={props.locale ?? 'es-MX'}
            onMesaLibre={props.onMesaLibre}
            onMesaOcupada={props.onMesaOcupada}
            onMarcarListo={props.onMarcarListo}
            onEntregarEnMesa={props.onEntregarEnMesa}
            onCobrar={props.onCobrar}
            onVerTicket={props.onVerTicket}
            onMarcarCuenta={props.onMarcarCuenta}
            onAgregarProductos={props.onAgregarProductos}
            onReunificarCuenta={props.onReunificarCuenta}
            onMoverPedido={props.onMoverPedido}
            onJuntarMesa={props.onJuntarMesa}
            onDividirCuenta={props.onDividirCuenta}
            onLiberarMesa={props.onLiberarMesa}
            onFueraServicio={props.onFueraServicio}
            onReactivar={props.onReactivar}
          />
        );
      })}
    </div>
  );
}

interface MesaCardProps {
  mesa: MesaVista;
  pedido: PedidoVista | null;
  compact: boolean;
  monedaSimbolo: string;
  locale: string;
  onMesaLibre?: (mesaId: number) => void;
  onMesaOcupada?: (mesaId: number, pedidoId: number | null) => void;
  /**
   * Sprint 111 — Mover el flujo de cocina sin salir de Mesas.
   *
   * Quien atiende sola no puede irse a la pantalla de Cocina para dar un paso y volver: cada
   * cambio de pantalla es perder de vista el salón.
   */
  onMarcarListo?: (pedidoId: number) => void;
  onEntregarEnMesa?: (pedidoId: number) => void;
  onCobrar?: (pedidoId: number) => void;
  onVerTicket?: (pedidoId: number) => void;
  onMarcarCuenta?: (pedidoId: number, solicitada: boolean) => void;
  onAgregarProductos?: (pedidoId: number, mesaId: number) => void;
  onReunificarCuenta?: (pedidoId: number) => void;
  onMoverPedido?: (pedidoId: number, mesaActualId: number) => void;
  onJuntarMesa?: (pedidoId: number, mesaPrincipalId: number) => void;
  onDividirCuenta?: (pedidoId: number) => void;
  onLiberarMesa?: (pedidoId: number, mesaNumero: number) => void;
  onFueraServicio?: (mesaId: number, mesaNumero: number) => void;
  onReactivar?: (mesaId: number) => void;
}

function MesaCard({
  mesa,
  pedido,
  compact,
  monedaSimbolo,
  locale,
  onMesaLibre,
  onMesaOcupada,
  onMarcarListo,
  onEntregarEnMesa,
  onCobrar,
  onVerTicket,
  onMarcarCuenta,
  onAgregarProductos,
  onReunificarCuenta,
  onMoverPedido,
  onJuntarMesa,
  onDividirCuenta,
  onLiberarMesa,
  onFueraServicio,
  onReactivar,
}: MesaCardProps) {
  // Sprint 9.2: estado FUERA DE SERVICIO trumps todo lo demás.
  const fueraServicio = mesa.estado === 'fuera_de_servicio';
  /*
    Sprint 172 — Ocupada también cuando el pedido no viene: a un mesero el servidor no le manda los pedidos
    de sus compañeros, pero la mesa sigue ocupada (lo dice su estado calculado). Sin esto, la mesa ajena se
    pintaba "Libre" con el botón de nuevo pedido.
  */
  const ajena = !fueraServicio && pedido === null && mesa.estado === 'ocupada';
  const ocupada = !fueraServicio && (pedido !== null || ajena);
  const cocinaLista = pedido?.estado === 'listo';
  const cuentaPedida = pedido?.cuentaSolicitada === true;

  // Estilos por estado
  let bgClass = 'bg-emerald-50 border-emerald-200';
  let badgeClass = 'bg-emerald-100 text-emerald-700';
  let badgeLabel = 'Libre';
  if (fueraServicio) {
    bgClass = 'bg-slate-100 border-slate-300';
    badgeClass = 'bg-slate-300 text-slate-800';
    badgeLabel = 'Fuera de servicio';
  } else if (cocinaLista) {
    bgClass = 'bg-amber-50 border-amber-300';
    badgeClass = 'bg-amber-200 text-amber-900';
    badgeLabel = 'Cocina lista';
  } else if (cuentaPedida) {
    bgClass = 'bg-rose-50 border-rose-300';
    badgeClass = 'bg-rose-200 text-rose-900';
    // Sprint 168 — Lo que importa de una cuenta pedida es que FALTA COBRAR.
    badgeLabel = 'Falta cobrar';
  } else if (ocupada) {
    bgClass = 'bg-orange-50 border-orange-200';
    badgeClass = 'bg-orange-100 text-orange-700';
    badgeLabel = 'Ocupada';
  }

  const fechaApertura =
    pedido?.fechaCreacion ?? mesa.pedidoActivoFechaCreacion ?? null;

  // ============================================================
  // VARIANTE COMPACTA (Dashboard) — sin kebab
  // ============================================================
  if (compact) {
    const handleClick = () => {
      if (fueraServicio) return; // No-op desde Dashboard.
      if (ajena) return; // Sprint 172 — la mesa de un compañero no se abre desde aquí.
      if (ocupada) {
        onMesaOcupada?.(mesa.id, pedido?.id ?? mesa.pedidoActivoId ?? null);
      } else {
        onMesaLibre?.(mesa.id);
      }
    };
    return (
      <button
        onClick={handleClick}
        disabled={fueraServicio}
        className={`card border-2 ${bgClass} flex flex-col items-start gap-1 p-3 text-left hover:shadow-md transition ${
          fueraServicio ? 'opacity-70 cursor-not-allowed' : ''
        }`}
      >
        <div className="flex w-full items-center justify-between">
          <div className="text-lg font-bold">#{mesa.numero}</div>
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase ${badgeClass}`}
          >
            {fueraServicio ? <Ban className="inline h-3 w-3" /> : badgeLabel}
          </span>
        </div>
        {ocupada && fechaApertura && (
          <div className="flex items-center gap-1 text-[11px] text-slate-600">
            <Clock className="h-3 w-3" />
            {tiempoTranscurrido(fechaApertura)}
          </div>
        )}
        {!ocupada && !fueraServicio && (
          <div className="text-[11px] text-slate-500">{mesa.capacidad} personas</div>
        )}
        {fueraServicio && (
          <div className="text-[11px] text-slate-500">No operativa</div>
        )}
      </button>
    );
  }

  // ============================================================
  // VARIANTE COMPLETA (página /mesas) — con kebab y acciones
  // ============================================================
  return (
    <div className={`card border-2 ${bgClass} space-y-3`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-2xl font-bold">Mesa #{mesa.numero}</div>
          {/* Sprint 12.1d — mostrar "N/M sillas" si hay pedido con comensales */}
          {ocupada && mesa.comensalesPedidoActivo != null ? (
            <div className="text-xs text-slate-500">
              {mesa.comensalesPedidoActivo}/{mesa.capacidad} sillas
              {mesa.capacidad > mesa.comensalesPedidoActivo && (
                <span className="text-emerald-700 ml-1">
                  · {mesa.capacidad - mesa.comensalesPedidoActivo} libres
                </span>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-500">{mesa.capacidad} personas</div>
          )}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${badgeClass}`}
        >
          {badgeLabel}
        </span>
      </div>

      {fueraServicio ? (
        <>
          <p className="text-xs text-slate-600">
            Esta mesa no aparece en el flujo de selección hasta que la reactives.
          </p>
          <ReactivarMesaButton onClick={() => onReactivar?.(mesa.id)} />
        </>
      ) : ocupada && !pedido ? (
        /*
          Sprint 172 (D-1) — Ocupada, pero el pedido no es de quien mira (el servidor no se lo manda a un
          mesero). Se dice quién la atiende y nada más: ni total ni botones. Así no se sienta a nadie
          encima y se sabe a quién pedirle que la entregue.
        */
        <div className="rounded-lg bg-white/60 p-3 text-sm text-slate-800">
          <div className="font-semibold">
            {mesa.meseroAtiendeNombre ? `La atiende ${mesa.meseroAtiendeNombre}` : 'La atiende otro compañero'}
          </div>
          <div className="mt-1 text-slate-700">Si te la va a pasar, que te la entregue desde su pantalla.</div>
        </div>
      ) : !ocupada || !pedido ? (
        <div className="space-y-2">
          <button onClick={() => onMesaLibre?.(mesa.id)} className="btn-primary w-full">
            <ArrowRight className="h-4 w-4" />
            Nuevo pedido
          </button>
          {/* Sprint 29.1 — antes oculto en el kebab; ahora visible. */}
          {onFueraServicio && (
            <button
              type="button"
              onClick={() => onFueraServicio(mesa.id, mesa.numero)}
              className="flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg border border-amber-200 px-3 py-2 text-sm font-medium text-amber-700 transition hover:bg-amber-50"
              title="Retirar la mesa del flujo de selección (mantenimiento, daño)"
            >
              <Ban className="h-4 w-4" />
              Marcar fuera de servicio
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Pedido</span>
              <span className="font-mono font-bold">
                #{String(pedido.numeroPedido).padStart(5, '0')}
              </span>
            </div>
            {/* Sprint 12.1d — mesero que atiende (referencia rápida) */}
            {mesa.meseroAtiendeNombre && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Atiende</span>
                <span className="font-medium">{mesa.meseroAtiendeNombre}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-slate-500 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Tiempo
              </span>
              <span className="font-medium">
                {tiempoTranscurrido(pedido.fechaCreacion)}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-current/10 pt-1.5 mt-1.5">
              <span className="text-slate-700 font-semibold">Total</span>
              <span className="font-mono font-bold">
                {formatPrecio(pedido.total, monedaSimbolo, locale)}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 text-[11px]">
            {cocinaLista && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-200 text-emerald-900 px-2 py-0.5 font-medium">
                <ChefHat className="h-3 w-3" />
                Llevar a mesa
              </span>
            )}
            {cuentaPedida && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-200 text-rose-900 px-2 py-0.5 font-medium">
                <Bell className="h-3 w-3" />
                Cobrar
              </span>
            )}
            {pedido.estado === 'pendiente_cocina' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 font-medium">
                <CircleDot className="h-3 w-3" />
                En cocina
              </span>
            )}
          </div>

          {/* Sprint 39 — Jerarquía visual por valor comercial:
              (1) PRIMARIAS: Agregar productos + Cobrar (verde/azul, grandes).
              (2) FRECUENTES: Pidió cuenta + Ver ticket.
              (3) EVENTUALES: Dividir / Mover / Juntar.
              Antes (Sprint 29.1) todo iba en un grid 3×2 plano del mismo color,
              lo que escondía la acción de "vender más" al mismo nivel que
              "mover mesa". */}

          {/*
            Sprint 111 — El paso del flujo que toca ahora, sin cambiar de sección.

            Petición del dueño: *"ella puede ver cuando algo ya está, avisarle al mesero que atiende
            la mesa y ver cómo el mesero lleva el pedido; ese flujo debería poder administrarlo sin
            estar cambiando de secciones"*. Antes, mover un pedido de "en cocina" a "listo" o a
            "entregado" obligaba a irse a la pantalla de Cocina y volver — y quien atiende sola
            pierde de vista el salón cada vez que cambia de pantalla.

            Va **arriba de todo y de ancho completo** porque es lo que caduca: agregar productos y
            cobrar pueden esperar, un plato listo enfriándose en la barra no. Y se muestra **una
            sola acción**, la del momento: nunca hay que elegir entre "listo" y "entregado".
          */}
          {onMarcarListo && (pedido.estado === 'pendiente_cocina' || pedido.estado === 'cocina') && (
            <button
              onClick={() => onMarcarListo(pedido.id)}
              className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 px-3 py-3 font-semibold text-white shadow-sm transition hover:bg-amber-600 active:scale-[0.98]"
              title="La cocina ya lo tiene listo"
            >
              <CheckCircle2 className="h-5 w-5" strokeWidth={3} />
              Ya está listo
            </button>
          )}
          {onEntregarEnMesa && pedido.estado === 'listo' && (
            <button
              onClick={() => onEntregarEnMesa(pedido.id)}
              className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-3 py-3 font-semibold text-white shadow-sm transition hover:bg-teal-700 active:scale-[0.98]"
              title="El mesero ya lo llevó a la mesa"
            >
              <CheckCircle2 className="h-5 w-5" strokeWidth={3} />
              Ya se lo llevaron a la mesa
            </button>
          )}

          {/*
            Sprint 168 — La cuenta ya está en la mano del cliente y el dinero todavía no entra.

            El 28/10 la Mesa 1 imprimió su cuenta a las 4:13 pm y nadie la cobró: la tarjeta seguía
            diciendo "Ocupada", igual que una mesa comiendo. Ahora dice cuánto lleva así, y a los
            ${MINUTOS_AVISO_CUENTA} minutos se pone en rojo (y aparece el aviso en todas las pantallas).
          */}
          {cuentaPedida && (() => {
            const min = minutosConCuenta(pedido.fechaCuentaSolicitada) ?? 0;
            const tarde = min >= MINUTOS_AVISO_CUENTA;
            return (
              <div
                className={`mb-2 rounded-lg border-2 px-3 py-2 text-sm font-semibold ${
                  tarde ? 'border-rose-500 bg-rose-100 text-rose-900' : 'border-amber-400 bg-amber-50 text-amber-900'
                }`}
                role="status"
              >
                🧾 Cuenta entregada {min < 1 ? 'hace un momento' : `hace ${min} min`} · <strong>falta cobrar</strong>
              </div>
            );
          })()}

          {/* (1) PRIMARIAS — vender más + cobrar */}
          <div className="grid grid-cols-2 gap-2">
            {onAgregarProductos ? (
              <button
                onClick={() => onAgregarProductos(pedido.id, mesa.id)}
                className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-3 font-semibold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.98]"
                title="Agregar más productos a este pedido"
              >
                <Plus className="h-5 w-5" strokeWidth={3} />
                Agregar productos
              </button>
            ) : (
              <span />
            )}
            {/*
              Sprint 64 — Sin `onCobrar` NO se pinta el botón. Antes se pintaba igual y el
              `?.` lo dejaba mudo: un botón grande, azul, que no hacía nada al presionarlo
              es la peor de las tres opciones — peor que estar deshabilitado y peor que no
              estar. Quien no cobra ahora ve el mismo hueco que cuando no puede agregar
              productos.
            */}
            {onCobrar ? (
              <button
                onClick={() => onCobrar(pedido.id)}
                className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-3 font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={pedido.estado === 'pagado' || pedido.estado === 'cancelado'}
              >
                <DollarSign className="h-5 w-5" strokeWidth={3} />
                Cobrar
              </button>
            ) : (
              <span />
            )}
          </div>

          {/* (2) + (3) — frecuentes y eventuales, neutros */}
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            <BotonAccionMesa
              icon={
                cuentaPedida ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <Bell className="h-4 w-4" />
                )
              }
              label={cuentaPedida ? 'Cuenta pedida' : 'Pidió cuenta'}
              tono={cuentaPedida ? 'activo' : 'normal'}
              onClick={() => onMarcarCuenta?.(pedido.id, !cuentaPedida)}
            />
            <BotonAccionMesa
              icon={<Eye className="h-4 w-4" />}
              label="Ver ticket"
              onClick={() => onVerTicket?.(pedido.id)}
            />
            {/* Un sub-pedido no se puede subdividir: ofrece "Volver a juntar". */}
            {pedido.pedidoPadreId ? (
              <BotonAccionMesa
                icon={<Combine className="h-4 w-4" />}
                label="Volver a juntar"
                tono="destacado"
                onClick={() => onReunificarCuenta?.(pedido.id)}
              />
            ) : (
              <BotonAccionMesa
                icon={<Split className="h-4 w-4" />}
                label="Dividir cuenta"
                onClick={() => onDividirCuenta?.(pedido.id)}
              />
            )}
            <BotonAccionMesa
              icon={<ArrowRightLeft className="h-4 w-4" />}
              label="Mover mesa"
              onClick={() => onMoverPedido?.(pedido.id, mesa.id)}
            />
            <BotonAccionMesa
              icon={<Link className="h-4 w-4" />}
              label="Juntar mesa"
              onClick={() => onJuntarMesa?.(pedido.id, mesa.id)}
            />
          </div>

          {/* Liberar cancela el pedido — destructivo: visible pero de-enfatizado. */}
          <button
            type="button"
            onClick={() => onLiberarMesa?.(pedido.id, mesa.numero)}
            className="flex w-full items-center justify-center gap-1 rounded-lg border border-red-200 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50"
            title="Cancela el pedido y libera la mesa (requiere PIN del dueño)"
          >
            <Unlock className="h-3.5 w-3.5" />
            Liberar mesa
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Sprint 29.1 — Botón de acción compacto para la tarjeta de mesa. Reemplaza el
 * menú kebab: todas las acciones quedan visibles. Ícono + etiqueta apilados,
 * ≥52px de alto (Ley de Fitts), etiqueta corta y legible.
 */
function BotonAccionMesa({
  icon,
  label,
  onClick,
  tono = 'normal',
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tono?: 'normal' | 'activo' | 'destacado';
}) {
  const tonoCls =
    tono === 'activo'
      ? 'border-rose-300 bg-rose-50 text-rose-700'
      : tono === 'destacado'
      ? 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[52px] flex-col items-center justify-center gap-0.5 rounded-lg border px-1 py-1.5 transition ${tonoCls}`}
    >
      {icon}
      <span className="text-center text-[10px] font-medium leading-tight">{label}</span>
    </button>
  );
}

function tiempoTranscurrido(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'recién';
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  return `${hr} h ${min % 60} min`;
}
